/**
 * The section-preview highlight: translucent full-width bands over the table,
 * one per CONTIGUOUS RUN of a section, same color for every run of the same
 * section — which is exactly how "one section, two places" reads at a glance.
 * On top of the bands, one rule per MOOD SPAN (F-139) across the table at the
 * top edge of the span's first row, labeled with the mood at the right end:
 * that edge is the exact sentence the mood changes on, and it can sit in the
 * middle of a band — a joke inside a topic — without breaking the band.
 *
 * A LAYER inside CameraLayer, mounted right after the grid so it paints above
 * the cells but under connectors, elements and comment pins. It renders in raw
 * board units from the SAME layout snapshot the grid published
 * (GridLayoutSnapshot — edges already reflect F-26 auto-grown heights), so the
 * bands can never drift from the rows they highlight, through pan, zoom, sort
 * or resize. Pointer-transparent for the same reason the design band is: a
 * preview that swallowed drags would read as a broken table, and every
 * interaction below it must keep working while the user checks the analysis.
 *
 * ZERO doc writes — this is the "show user first" half of the feature; the
 * doc changes only if they accept (apply.ts).
 */
import { useStore } from "zustand";

import { withAlpha } from "../../core/color.js";
import type { BoardSession } from "../../core/session.js";
import type { GridLayoutSnapshot } from "../board/GridTable.js";
import { moodMarkers, sectionBands } from "./analyze.js";
import { sectionsUi } from "./store.js";

export interface SectionsOverlayLayerProps {
  session: BoardSession;
  /** Latest grid layout pass — positions the bands. May be null. */
  layout: GridLayoutSnapshot | null;
}

export function SectionsOverlayLayer({ session, layout }: SectionsOverlayLayerProps) {
  const target = useStore(sectionsUi, (s) => s.target);
  const analysis = useStore(sectionsUi, (s) => s.analysis);
  if (target === null || analysis === null || layout === null) return null;
  if (target.boardId !== session.boardId || layout.sheetId !== target.sheetId) return null;

  const bands = sectionBands(analysis.sections, layout);
  const markers = moodMarkers(analysis.moods, layout);
  return (
    <>
      {bands.map((band) => (
        <div
          key={band.key}
          className="sections-band"
          style={{
            left: band.x,
            top: band.y,
            width: band.w,
            height: band.h,
            background: withAlpha(band.color, 0.3),
          }}
        >
          <span className="sections-band-label" style={{ background: band.color }}>
            {band.title}
          </span>
        </div>
      ))}
      {markers.map((marker) => (
        <div
          key={marker.key}
          className="sections-mood-line"
          style={{ left: marker.x, top: marker.y, width: marker.w }}
        >
          <span className="sections-mood-label">{marker.mood}</span>
        </div>
      ))}
    </>
  );
}
